import * as React from 'react';
import { FC, useEffect, useState } from 'react';
import * as d3 from 'd3';
import RaceTrackPointView from '@utmb/types/view/RaceTrackPointView';
import { RaceTrackIcon } from '../RaceTrackIcon';
import { renderToString } from 'react-dom/server';
import { AvailableIcon, SvgRaceProfile } from '@utmb/types/map';
import RaceProfileDialog from '@utmb/features/race-track/profile/RaceProfileDialog';
import styles from './race-profile.module.scss';
import { getPointIcons } from '@utmb/utils/point';
import { useRaceTrack } from '@utmb/contexts/RaceTrackContext';
import RaceLegend from '@utmb/features/race-track/profile/RaceLegend';
import { useRouter } from 'next/router';
import { usePreference } from '@utmb/contexts/PreferenceContext';
import { formatUnit } from '@utmb/utils/unit';
import { UnitEnum } from '@utmb/types/unit-enum';

const HEIGHT_IN_PX = 630;

const PADDING_TOP = 30;
const MARGIN_X = 5;

const TOP_BAR_HEIGHT = 140;
const TOP_BAR_BOUNDARY = 60;
const TOP_BAR_SEGMENT = 2;
const TOP_BAR_POINT = 45;
const TOP_BAR_SERVICES = 120;

const PROFILE_HEIGHT = 100;
const DIVIDE_HEIGHT = 10;
const SEGMENT_HEIGHT = 15;
const BOTTOM_BAR_HEIGHT_ONE_LINE = 50;
const BOTTOM_BAR_HEIGHT_TWO_LINE = 75;
const BOTTOM_BAR_POINT = 30;
const WIDTH = 800;

const COLOR_WHITE = '#FFFFFF';
const COLOR_GREY = '#1E1E1E';
export const COLOR_LIGHT_GREY = '#ecedee';
export const COLOR_DARK_GREY = 'var(--utmb-color-default)';
const COLOR_GREY_FONT = '#9BA7B0';
const COLOR_DARK_GREY_FONT = '#1E272E';

const FONT_FUTURA_BOOK = 'var(--utmb-font-futura-book)';
const FONT_FUTURA_HEAVY = 'var(--utmb-font-futura-heavy)';
const FONT_FUTURA_LIGHT = 'var(--utmb-font-futura-light)';
const FONT_OXANIUM = 'var(--utmb-font-oxanium)';

const SVG_ID_PROFILE = 'elevationProfile';
export const SVG_ID_SVG = `${SVG_ID_PROFILE}_svg`;

const ISOLINE_INTERVAL_MIN = 4;
const ISOLINE_INTERVAL_MAX = 100;
const ISOLINE_INTERVAL = 8;
const ISOLINE_POLYNOMIAL_COEFFICIENT = 35;

interface BoundaryFlag {
    code: string;
    svg: any;
}

// noinspection JSUnusedGlobalSymbols
export const RaceProfile: FC = () => {
    const {
        track: { profile, distance, maxElevation, minElevation, gridElevation, points, hasElevationMargin },
        color,
        isDark,
    } = useRaceTrack();

    const {
        preferences: { preferredUnit },
    } = usePreference();

    const {
        query: { padding },
    } = useRouter();

    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [dialogPoint, setDialogPoint] = useState<RaceTrackPointView>(points[0]);

    const drawChartAsync = async (): Promise<void> => {
        const flagsCodes: string[] = [
            ...new Set(
                points
                    .filter(({ boundary }: RaceTrackPointView) => boundary)
                    .map(({ boundary }: RaceTrackPointView) => boundary.split('-'))
                    .flat()
            ),
        ];

        const flags = (await Promise.all(
            flagsCodes.map(async (code) => {
                const request = await fetch(`https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/4.1.5/flags/4x3/${code.toLowerCase()}.svg`);
                const svg = await request.text();
                return {
                    code,
                    svg,
                };
            })
        )) as BoundaryFlag[];

        drawChart(
            profile,
            distance,
            maxElevation,
            minElevation,
            color,
            isDark,
            gridElevation,
            points,
            hasElevationMargin,
            flags,
            setIsDialogOpen,
            setDialogPoint,
            padding as string
        );
    };

    useEffect(() => {
        drawChartAsync().then();
    }, []);

    const drawChart = (
        profile: number[][],
        distance: number,
        maxElevation: number,
        minElevation: number,
        color: string,
        isDark: boolean,
        grid: number[],
        points: RaceTrackPointView[],
        hasElevationMargin: boolean,
        flags: BoundaryFlag[],
        setIsDialogOpen: (isOpen: boolean) => void,
        setDialogPoint: (point: RaceTrackPointView) => void,
        padding = '0'
    ): void => {
        const widthCoefficient = calculateWidthCoefficient(distance, points);

        const altitudeMargin: number = hasElevationMargin ? 40 : 0;

        const calculatedWidth = WIDTH * widthCoefficient + altitudeMargin;

        const isolines: number[][][] = generateIsolines(profile, minElevation, distance);

        const xScale: d3.ScaleLinear<number, number> = d3.scaleLinear().domain([0, distance]).range([altitudeMargin, calculatedWidth]);

        const yScale = d3.scaleLinear().domain([minElevation, maxElevation]).range([PROFILE_HEIGHT, 0]);

        const height = PADDING_TOP + TOP_BAR_HEIGHT + PROFILE_HEIGHT + DIVIDE_HEIGHT + SEGMENT_HEIGHT + BOTTOM_BAR_HEIGHT_TWO_LINE;

        d3.select(`#${SVG_ID_SVG}`).remove();

        const svg = d3
            .select(`#${SVG_ID_PROFILE}`)
            .append('svg')
            .attr('id', SVG_ID_SVG)
            .attr('viewBox', '0 0 ' + (calculatedWidth + 2 * MARGIN_X) + ' ' + height)
            .style('height', `${HEIGHT_IN_PX}px`)
            .style('display', 'block')
            .style('margin', 'auto')
            .style('padding', `${padding}px 0`)
            .attr('preserveAspectRatio', 'xMinYMid')
            .append('g')
            .attr('transform', `translate(${MARGIN_X}, 0)`);

        const svgRaceProfile: SvgRaceProfile = {
            svg,
            xScale,
            yScale,
            color,
            isDark,
            altitudeMargin,
        };

        drawBackground(svgRaceProfile);

        drawAltitudeLines(grid, calculatedWidth, svgRaceProfile);

        drawProfileAndIsolines(profile, isolines, svgRaceProfile);

        drawSegments(points, svgRaceProfile);

        placeSegmentName(points, svgRaceProfile);

        drawPoints(points, svgRaceProfile, flags, setIsDialogOpen, setDialogPoint);

        drawSegmentNamePin(svgRaceProfile);

        drawSegmentName(points, svgRaceProfile);

        drawTooltip(svgRaceProfile);
    };

    const calculateSegmentLabelsLineHeight = (points: RaceTrackPointView[]): number =>
        points.filter((point) => !point.isLabelFirstLine).length > 0 ? BOTTOM_BAR_HEIGHT_TWO_LINE : BOTTOM_BAR_HEIGHT_ONE_LINE;

    const calculateWidthCoefficient = (distance: number, points: RaceTrackPointView[]): number => {
        const distances: { distance: number; isSegmentChevron: boolean }[] = points
            .filter((point) => point.isSegment || point.boundary)
            .map(({ distance, isSegmentChevron }) => ({ distance, isSegmentChevron }));

        const relativeDistances: { distance: number; isSegmentChevron: boolean }[] = distances
            .map((d, i: number) => (i === 0 ? undefined : { distance: d.distance - distances[i - 1].distance, isSegmentChevron: d.isSegmentChevron }))
            .filter(Boolean) as { distance: number; isSegmentChevron: boolean }[];

        const relativeDistanceEquivalent: number[] = relativeDistances.map((d) => d.distance * (d.isSegmentChevron ? 1 : 1.5));

        const minRelativeDistance = Math.min(...relativeDistanceEquivalent);

        const widthCoefficient2 = Math.round((distance / (15 * minRelativeDistance)) * 100) / 100;

        return Math.max(widthCoefficient2, 1);
    };

    const generateIsolines = (profile: number[][], minElevation: number, distance: number): number[][][] => {
        const intervals: number[] = [];
        for (let i = ISOLINE_INTERVAL_MAX - ISOLINE_INTERVAL; i > ISOLINE_INTERVAL_MIN; i = i - ISOLINE_INTERVAL) {
            intervals.push(i / 100);
        }
        intervals.push(0);

        const formulaX = (x: number, ratio: number): number => {
            const M = ISOLINE_POLYNOMIAL_COEFFICIENT * ratio;

            return (x * x) / ((2 * M + 1) * distance) + (x * 2 * M) / (2 * M + 1);
        };

        const formulaY = (y: number, ratio: number): number => minElevation + ratio * (y - minElevation);

        return intervals.map((interval) => profile.map(([x, y]: number[]) => [formulaX(x, interval), formulaY(y, interval)]));
    };

    const drawSegmentLabel = (
        startDistance: number,
        finishDistance: number,
        { svg, xScale, altitudeMargin }: SvgRaceProfile,
        isSegmentChevron: boolean
    ): void => {
        const segmentDistance = finishDistance - startDistance;

        const distanceLabel = formatUnit(preferredUnit, 'distance', segmentDistance / 1000, UnitEnum.METRIC, true);

        svg.append('rect')
            .attr('x', xScale(startDistance))
            .attr('y', PADDING_TOP + TOP_BAR_HEIGHT + PROFILE_HEIGHT + DIVIDE_HEIGHT)
            .attr('width', `${xScale(segmentDistance) - altitudeMargin}`)
            .attr('height', `${SEGMENT_HEIGHT}`)
            .style('fill', COLOR_WHITE);

        svg.append('text')
            .attr('x', xScale(startDistance + segmentDistance / 2))
            .attr('y', PADDING_TOP + TOP_BAR_HEIGHT + PROFILE_HEIGHT + DIVIDE_HEIGHT + SEGMENT_HEIGHT / 2)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('dy', 1)
            .style('fill', COLOR_DARK_GREY_FONT)
            .style('fill-opacity', 1)
            .style('font-size', '7px')
            .style('font-family', FONT_FUTURA_BOOK)
            .text(`${distanceLabel} ${preferredUnit === UnitEnum.IMPERIAL ? 'M' : 'km'}`);

        if (isSegmentChevron) {
            svg.append('svg')
                .attr('x', xScale(startDistance) + 4)
                .attr('y', PADDING_TOP + TOP_BAR_HEIGHT + PROFILE_HEIGHT + DIVIDE_HEIGHT + SEGMENT_HEIGHT / 2 - 4)
                .attr('height', '8px')
                .attr('width', '8px')
                .attr('viewBox', '0 0 24 24')
                .html(chevronLeft);

            svg.append('svg')
                .attr('x', xScale(startDistance + segmentDistance) - 12)
                .attr('y', PADDING_TOP + TOP_BAR_HEIGHT + PROFILE_HEIGHT + DIVIDE_HEIGHT + SEGMENT_HEIGHT / 2 - 4)
                .attr('height', '8px')
                .attr('width', '8px')
                .attr('viewBox', '0 0 24 24')
                .html(chevronRight);
        }
    };

    const drawTotalDistanceLabel = (nextDistance: number, { svg, xScale, color, isDark }: SvgRaceProfile): void => {
        const distance = formatUnit(preferredUnit, 'distance', nextDistance / 1000, UnitEnum.METRIC, true); //roundToDistanceInKm(nextDistance);

        const width = (distance.length + (preferredUnit === UnitEnum.IMPERIAL ? 1.7 : 2.7) - (distance.includes('.') ? 0.5 : 0)) * 6;
        svg.append('rect')
            .attr('x', xScale(nextDistance) - width)
            .attr('y', PADDING_TOP + TOP_BAR_HEIGHT + PROFILE_HEIGHT + DIVIDE_HEIGHT + SEGMENT_HEIGHT)
            .attr('width', `${width}`)
            .attr('height', `${SEGMENT_HEIGHT}`)
            .style('fill', color);

        svg.append('text')
            .attr('x', xScale(nextDistance) - width / 2)
            .attr('y', PADDING_TOP + TOP_BAR_HEIGHT + PROFILE_HEIGHT + DIVIDE_HEIGHT + SEGMENT_HEIGHT + SEGMENT_HEIGHT / 2)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('dy', 1)
            .style('fill', isDark ? COLOR_WHITE : COLOR_DARK_GREY)
            .style('fill-opacity', 1)
            .style('font-size', '8px')
            .style('font-family', FONT_FUTURA_BOOK)
            .style('text-transform', 'uppercase')
            .text(`${distance} ${preferredUnit === UnitEnum.IMPERIAL ? 'M' : 'km'}`);
    };

    const drawProfileAndIsolines = (profile: number[][], isopleths: number[][][], { svg, xScale, yScale, color }: SvgRaceProfile): void => {
        // Choose curve here : https://github.com/d3/d3/blob/main/API.md#curves
        const profileArea = d3
            .area<number[]>()
            .x(([x]: number[]) => xScale(x))
            .y(([, y]: number[]) => yScale(y) + PADDING_TOP + TOP_BAR_HEIGHT)
            .curve(d3.curveCatmullRom.alpha(0));

        const profileAreaData = profileArea(profile);

        svg.append('path')
            .attr('d', profileAreaData)
            .style('stroke', color)
            .style('stroke-opacity', 0.75)
            .style('stroke-width', 0.75)
            .style('fill', COLOR_LIGHT_GREY);

        isopleths.map((isopleth: number[][]) => {
            const data = profileArea(isopleth);

            svg.append('path').attr('d', data).style('stroke', color).style('stroke-opacity', 0.75).style('stroke-width', 0.75).style('fill', COLOR_LIGHT_GREY);
        });
    };

    const drawAltitudeLines = (grid: number[], width: number, { svg, yScale }: SvgRaceProfile): void => {
        grid.map((ele) => {
            const eleLabel = formatUnit(preferredUnit, 'elevation', ele);

            svg.append('line')
                .attr('x1', 0)
                .attr('y1', yScale(ele) + PADDING_TOP + TOP_BAR_HEIGHT)
                .attr('x2', width)
                .attr('y2', yScale(ele) + PADDING_TOP + TOP_BAR_HEIGHT)
                .style('stroke', COLOR_GREY_FONT)
                .style('stroke-width', 0.25);

            svg.append('text')
                .attr('x', 0)
                .attr('y', yScale(ele) + PADDING_TOP + TOP_BAR_HEIGHT)
                .attr('dy', '-0.5em')
                .style('fill', COLOR_GREY_FONT)
                .style('fill-opacity', 0.75)
                .style('font-size', '8px')
                .style('font-family', FONT_FUTURA_LIGHT)
                .text(`${eleLabel}`);
        });
    };

    const drawSegments = (points: RaceTrackPointView[], svgRaceProfile: SvgRaceProfile): void => {
        const segments = points.filter(({ isSegment }: RaceTrackPointView) => isSegment);

        segments.map(({ distance }: RaceTrackPointView, index: number) => {
            if (index < segments.length - 1) {
                const nextDistance = segments[index + 1].distance;
                const isSegmentChevron = segments[index + 1].isSegmentChevron;

                drawTotalDistanceLabel(nextDistance, svgRaceProfile);

                drawSegmentLabel(distance, nextDistance, svgRaceProfile, isSegmentChevron);
            }
        });
    };

    const placeSegmentName = (points: RaceTrackPointView[], { svg, xScale }: SvgRaceProfile): void => {
        points.map((point: RaceTrackPointView) => {
            const { distance, isSegment, shortName, isLabelRight, isLabelFirstLine } = point;

            if (isSegment) {
                const labelOffset = isLabelFirstLine ? 0 : 25;
                // noinspection DuplicatedCode
                svg.append('text')
                    .attr('x', xScale(distance))
                    .attr('y', PADDING_TOP + TOP_BAR_HEIGHT + PROFILE_HEIGHT + DIVIDE_HEIGHT + SEGMENT_HEIGHT + BOTTOM_BAR_POINT + labelOffset)
                    .attr('dy', 2)
                    .attr('dx', isLabelRight ? 10 : -10)
                    .attr('class', 'segmentName')
                    .text(shortName)
                    .attr('text-anchor', isLabelRight ? 'start' : 'end')
                    .style('font-size', '8px')
                    .style('font-family', FONT_FUTURA_BOOK);
            }
        });
    };

    const drawSegmentNamePin = ({ svg }: SvgRaceProfile): void => {
        const selection: any = svg.selectAll('.segmentName');

        selection.nodes().map((node: SVGTextElement) => {
            const { x, y, width, height } = node.getBBox();

            svg.append('rect')
                .attr('x', x - 5)
                .attr('y', y - 3)
                .style('width', `${width + 10}px`)
                .style('height', `${height + 6}px`)
                .style('fill', COLOR_WHITE)
                .style('rx', '2px')
                .style('ry', '2px')
                .style('-webkit-filter', 'drop-shadow( 0px 2px 2px rgba(0, 0, 0, .5)')
                .style('filter', 'drop-shadow( 0px 2px 2px rgba(0, 0, 0, .5)');
        });
    };
    const drawSegmentName = (points: RaceTrackPointView[], { svg, xScale, color }: SvgRaceProfile): void => {
        points
            .filter(({ isSegment }: RaceTrackPointView) => isSegment)
            .map(({ isLabelFirstLine, distance, shortName, isLabelRight, cutoff }: RaceTrackPointView) => {
                const labelOffset = isLabelFirstLine ? 0 : 25;

                // noinspection DuplicatedCode
                svg.append('text')
                    .attr('x', xScale(distance))
                    .attr('y', PADDING_TOP + TOP_BAR_HEIGHT + PROFILE_HEIGHT + DIVIDE_HEIGHT + SEGMENT_HEIGHT + BOTTOM_BAR_POINT + labelOffset)
                    .attr('dy', 2)
                    .attr('dx', isLabelRight ? 10 : -10)
                    .attr('class', 'segmentName')
                    .text(shortName)
                    .attr('text-anchor', isLabelRight ? 'start' : 'end')
                    .style('fill', COLOR_GREY)
                    .style('fill-opacity', 0.75)
                    .style('font-size', '8px')
                    .style('font-family', FONT_FUTURA_BOOK);

                cutoff &&
                    svg
                        .append('text')
                        .attr('x', xScale(distance))
                        .attr('y', PADDING_TOP + TOP_BAR_HEIGHT + PROFILE_HEIGHT + DIVIDE_HEIGHT + SEGMENT_HEIGHT + BOTTOM_BAR_POINT + 14 + labelOffset)
                        .attr('dy', 6)
                        .attr('dx', isLabelRight ? 6 : -6)
                        .text(cutoff)
                        .attr('text-anchor', isLabelRight ? 'start' : 'end')
                        .style('fill', color)
                        .style('fill-opacity', 1)
                        .style('font-size', '8px')
                        .style('font-weight', 300)
                        .style('font-family', FONT_OXANIUM);
            });
    };

    const drawPoints = (
        points: RaceTrackPointView[],
        { svg, yScale, xScale, color, isDark }: SvgRaceProfile,
        flags: BoundaryFlag[],
        setIsDialogOpen: (isOpen: boolean) => void,
        setDialogPoint: (point: RaceTrackPointView) => void
    ): void => {
        points.map((point: RaceTrackPointView, index: number) => {
            const { distance, elevation, isSegment, uid, isLabelFirstLine, shortName, isAssistance, boundary } = point;

            const onClick = (): void => {
                setDialogPoint(point);
                setIsDialogOpen(true);
            };

            const isStart: boolean = index === 0;
            const isFinish: boolean = index === points.length - 1;
            const isStartOrFinish: boolean = isStart || isFinish;

            svg.append('circle')
                .attr('r', 3.5)
                .attr('cx', xScale(distance))
                .attr('cy', yScale(elevation) + PADDING_TOP + TOP_BAR_HEIGHT)
                .attr('class', distance)
                .style('fill', color)
                .style('cursor', 'crosshair')
                .on('mouseover', (event: Event) => mouseover(event))
                .on('mousemove', (event: Event) => mousemove(event, point))
                .on('mouseleave', mouseleave)
                .on('click', onClick);

            if (!isStartOrFinish) {
                svg.append('line')
                    .attr('x1', xScale(distance))
                    .attr('y1', yScale(elevation) + PADDING_TOP + TOP_BAR_HEIGHT)
                    .attr('x2', xScale(distance))
                    .attr('y2', isSegment ? PADDING_TOP + TOP_BAR_SEGMENT : PADDING_TOP + TOP_BAR_POINT)
                    .style('stroke', color)
                    .style('stroke-width', 0.25);
            }

            if (boundary) {
                const [code1, code2] = boundary.split('-').map((code) => code);

                svg.append('circle')
                    .attr('r', 12)
                    .attr('cx', xScale(distance))
                    .attr('cy', PADDING_TOP + TOP_BAR_BOUNDARY)
                    .style('fill', COLOR_WHITE);

                svg.append('svg')
                    .attr('x', xScale(distance) - 5)
                    .attr('y', PADDING_TOP + TOP_BAR_BOUNDARY - 9)
                    .attr('height', '8px')
                    .attr('width', '10px')
                    .attr('viewBox', '0 0 640 480')
                    .html(flags.find(({ code }) => code === code1)!.svg);

                svg.append('svg')
                    .attr('x', xScale(distance) - 5)
                    .attr('y', PADDING_TOP + TOP_BAR_BOUNDARY)
                    .attr('height', '8px')
                    .attr('width', '10px')
                    .attr('viewBox', '0 0 640 480')
                    .html(flags.find(({ code }) => code === code2)!.svg);
            }

            if (isSegment) {
                const segmentLabelsLineHeight = calculateSegmentLabelsLineHeight(points);

                svg.append('line')
                    .attr('x1', xScale(distance))
                    .attr('y1', yScale(elevation) + PADDING_TOP + TOP_BAR_HEIGHT)
                    .attr('x2', xScale(distance))
                    .attr('y2', PADDING_TOP + TOP_BAR_HEIGHT + PROFILE_HEIGHT + DIVIDE_HEIGHT + SEGMENT_HEIGHT + segmentLabelsLineHeight)
                    .style('stroke', isStartOrFinish ? COLOR_GREY : color)
                    .style('stroke-width', 0.25);

                const circleOffset = isLabelFirstLine ? 0 : 24;

                svg.append('circle')
                    .attr('cx', xScale(distance))
                    .attr('cy', PADDING_TOP + TOP_BAR_HEIGHT + PROFILE_HEIGHT + DIVIDE_HEIGHT + SEGMENT_HEIGHT + BOTTOM_BAR_POINT + circleOffset)
                    .attr('r', 1.5)
                    .style('fill', color);

                if (!isStartOrFinish) {
                    if (uid) {
                        svg.append('circle')
                            .attr('r', 14)
                            .attr('cx', xScale(distance))
                            .attr('cy', PADDING_TOP + 14)
                            .style('cursor', 'pointer')
                            .style('fill', COLOR_WHITE)
                            .style('-webkit-filter', 'drop-shadow( 0px 2px 2px rgba(0, 0, 0, .5)')
                            .style('filter', 'drop-shadow( 0px 2px 2px rgba(0, 0, 0, .5)')
                            .on('click', onClick);

                        svg.append('text')
                            .attr('x', xScale(distance))
                            .attr('y', PADDING_TOP + 14)
                            .attr('dy', 2)
                            .text(uid)
                            .attr('text-anchor', 'middle')
                            .attr('dominant-baseline', 'middle')
                            .style('cursor', 'pointer')
                            .style('fill', COLOR_DARK_GREY_FONT)
                            .style('fill-opacity', 1)
                            .style('font-size', '10px')
                            .style('font-family', FONT_FUTURA_HEAVY)
                            .on('click', onClick);

                        isAssistance &&
                            svg
                                .append('rect')
                                .attr('x', xScale(distance) - 13)
                                .attr('y', PADDING_TOP + 1)
                                .style('cursor', 'pointer')
                                .style('width', '26px')
                                .style('height', '26px')
                                .style('fill', 'none')
                                .style('rx', '140px')
                                .style('ry', '140px')
                                .style('stroke', color)
                                .style('stroke-width', '1.5px')
                                .style('stroke-dasharray', '3, 2')
                                .style('stroke-dashoffset', 1)
                                .style('stroke-linecap', 'butt')
                                .on('click', onClick);
                    }

                    const icons: AvailableIcon[] = getPointIcons(point);

                    if (icons.length > 0) {
                        const radius = 12;
                        const serviceHeight = 20;
                        const onClick = (): void => {
                            setDialogPoint(point);
                            setIsDialogOpen(true);
                        };

                        if (icons.length > 1) {
                            svg.append('rect')
                                .attr('x', xScale(distance) - radius)
                                .attr('y', TOP_BAR_SERVICES)
                                .attr('width', `${radius * 2}px`)
                                .attr('height', `${serviceHeight}px`)
                                .style('fill', isDark ? COLOR_WHITE : COLOR_DARK_GREY_FONT)
                                .style('cursor', 'pointer')
                                .on('click', onClick);

                            svg.append('circle')
                                .attr('r', radius)
                                .attr('cx', xScale(distance))
                                .attr('cy', TOP_BAR_SERVICES + serviceHeight)
                                .style('fill', isDark ? COLOR_WHITE : COLOR_DARK_GREY_FONT)
                                .style('cursor', 'pointer')
                                .on('click', onClick);
                        }

                        svg.append('circle')
                            .attr('r', radius)
                            .attr('cx', xScale(distance))
                            .attr('cy', TOP_BAR_SERVICES)
                            .style('fill', isDark ? COLOR_WHITE : COLOR_DARK_GREY_FONT)
                            .style('cursor', 'pointer')
                            .on('click', onClick);

                        svg.append('svg')
                            .attr('x', xScale(distance) - radius / 2 - 1)
                            .attr('y', TOP_BAR_SERVICES - 7)
                            .attr('height', '14px')
                            .attr('width', '14px')
                            .attr('viewBox', '0 0 24 24')
                            .html(renderToString(<RaceTrackIcon icon={icons[0]} color={color} />))
                            .style('cursor', 'pointer')
                            .on('click', onClick);

                        if (icons.length > 1) {
                            svg.append('text')
                                .attr('x', xScale(distance) - 2)
                                .attr('y', TOP_BAR_SERVICES + serviceHeight - 2)
                                .attr('dx', '-4px')
                                .attr('dy', '4px')
                                .text(`+${icons.length - 1}`)
                                .style('fill', color)
                                .style('font-size', '10px')
                                .style('font-weight', 600)
                                .style('font-family', FONT_OXANIUM)
                                .style('cursor', 'pointer')
                                .on('click', onClick);
                            svg.append('rect')
                                .attr('x', xScale(distance) - 5)
                                .attr('y', TOP_BAR_SERVICES + serviceHeight + 4)
                                .attr('width', '10px')
                                .attr('height', '2px')
                                .style('fill', color)
                                .style('cursor', 'pointer')
                                .on('click', onClick);
                        }
                    }
                }
            } else {
                const x = xScale(distance);
                const y = PADDING_TOP + TOP_BAR_POINT;
                svg.append('text')
                    .attr('x', xScale(distance))
                    .attr('y', y)
                    .attr('transform-origin', `${x} ${y}`)
                    .attr('dy', '2px')
                    .attr('dx', '5px')
                    .text(shortName)
                    .style('fill', COLOR_DARK_GREY_FONT)
                    .style('fill-opacity', 0.75)
                    .style('font-size', '9px')
                    .style('font-family', FONT_FUTURA_LIGHT)
                    .style('transform', 'rotate(-90deg)');
            }
        });
    };

    const mouseover = (event: any): void => {
        d3.select(`#tooltip`).style('opacity', 1);
        d3.select(`#tooltip_text`).style('opacity', 1);
        d3.select(event.target)
            .attr('r', 4)
            .style('-webkit-filter', 'drop-shadow( 3px 3px 2px rgba(0, 0, 0, .2)')
            .style('filter', 'drop-shadow( 3px 3px 2px rgba(0, 0, 0, .2)')
            .style('stroke', COLOR_WHITE)
            .style('stroke-width', 2)
            .style('opacity', 1);
    };

    const mousemove = (event: any, point: RaceTrackPointView): void => {
        const x = d3.pointer(event)[0];
        const y = d3.pointer(event)[1];

        d3.select(`#tooltip`)
            .attr('x', x + 10)
            .attr('y', y - 6);
        d3.select(`#tooltip_text`)
            .text(`${formatUnit(preferredUnit, 'elevation', point.elevation)}`)
            .attr('x', x + 30)
            .attr('y', y + 3);
    };

    const mouseleave = (event: any): void => {
        d3.select('#tooltip').style('opacity', 0);
        d3.select('#tooltip_text').style('opacity', 0);
        d3.select(event.target).attr('r', 3).style('stroke', 'none').style('-webkit-filter', 'none').style('filter', 'none').style('opacity', 1);
    };

    const drawBackground = ({ svg }: SvgRaceProfile): void => {
        svg.append('rect')
            .attr('fill', 'transparent') //isDark ? COLOR_LIGHT_GREY : COLOR_DARK_GREY)
            .style('width', '100%')
            .style('height', '100%');
    };

    const drawTooltip = ({ svg, xScale, yScale }: SvgRaceProfile): void => {
        svg.append('rect')
            .attr('id', 'tooltip')
            .style('opacity', 0)
            .style('width', '40px')
            .style('height', '15px')
            .attr('fill', COLOR_WHITE)
            .attr('rx', '5px')
            .attr('ry', '5px')
            .attr('x', xScale(0))
            .attr('y', yScale(0) + PADDING_TOP + TOP_BAR_HEIGHT)
            .style('-webkit-filter', 'drop-shadow( 2px 2px 1px rgba(0, 0, 0, .2)')
            .style('filter', 'drop-shadow( 2px 2px 1px rgba(0, 0, 0, .2)');

        svg.append('text')
            .attr('id', 'tooltip_text')
            .style('opacity', 0)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('x', 0)
            .attr('y', 0)
            .style('fill', COLOR_DARK_GREY_FONT)
            .style('fill-opacity', 1)
            .style('font-size', '9px')
            .style('font-family', FONT_FUTURA_HEAVY);
    };

    const chevronRight =
        '<svg viewBox="0 0 24 24"><path d="M0,0h24v24H0V0z" fill="none"/><polygon points="6.23,20.23 8,22 18,12 8,2 6.23,3.77 14.46,12" fill={COLOR_LIGHT_GREY} fill-opacity="0.25"/></svg>';

    const chevronLeft =
        '<svg viewBox="0 0 24 24"><path d="M0 0h24v24H0z" fill="none"/><path d="M11.67 3.87L9.9 2.1 0 12l9.9 9.9 1.77-1.77L3.54 12z" fill={COLOR_LIGHT_GREY} fill-opacity="0.25"/></svg>';

    return (
        <div className={styles.graph_wrapper} id={SVG_ID_PROFILE} style={isDark ? {} : { backgroundColor: 'var(--utmb-color-default)' }}>
            <RaceLegend />
            <RaceProfileDialog isOpen={isDialogOpen} close={() => setIsDialogOpen(false)} point={dialogPoint} />
        </div>
    );
};
